import type { ReactNode } from 'react'
import { Card } from './Surface'

/** Says what would put something here. "No data" is not a state, it is a shrug. */
export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode
  title: string
  body: string
  action?: ReactNode
}) {
  return (
    <Card className="mx-message">
      {icon && (
        <div className="mx-message-glyph" aria-hidden="true">
          {icon}
        </div>
      )}
      <p className="mx-message-title">{title}</p>
      <p className="mx-message-body">{body}</p>
      {action}
    </Card>
  )
}

/**
 * The one place status codes turn into words, so no screen has to decide again.
 *
 * 404 is always "not found" and never "no permission": the API answers 404 for
 * "does not exist" and for "is not yours" on purpose, because which ids exist
 * inside other schools is exactly what that hides.
 *
 * 503 is "not switched on yet", not an outage — billing answers it while
 * unconfigured, and calling that an error sends someone looking for a fault.
 */
export function ErrorState({
  error,
  onRetry,
  notFoundLabel = 'Not found',
}: {
  error: unknown
  onRetry?: () => void
  notFoundLabel?: string
}) {
  const status = typeof error === 'object' && error !== null && 'status' in error
    ? (error as { status: number }).status
    : undefined
  const name = error instanceof Error ? error.name : ''
  const message = error instanceof Error ? error.message : 'Something went wrong.'

  if (name === 'SessionExpiredError') {
    return (
      <EmptyState
        title="Session expired"
        body="Sign in again to pick up where you left off. Nothing has been lost."
      />
    )
  }

  if (status === 404) {
    return (
      <EmptyState
        title={notFoundLabel}
        body="It may have been deleted, or the link may be out of date."
      />
    )
  }

  if (status === 503) {
    return (
      <EmptyState
        title="Not switched on yet"
        body="This part of Modulus isn't available on your account yet. Nothing is broken."
      />
    )
  }

  if (status === 429) {
    // The API writes these for a student to read. Show them as written.
    return <EmptyState title="Slow down a moment" body={message} />
  }

  // `TypeError: Failed to fetch` is the browser's message for "the connection did
  // not happen" — offline, DNS, or a dev server that went away. It has no status
  // and reads as a bug in the app, which it almost never is. Say what it means.
  if (error instanceof TypeError) {
    return (
      <EmptyState
        title="Couldn't reach the server"
        body={
          navigator.onLine
            ? 'The connection failed rather than being refused. If this is a local dev build, check that the servers are still running.'
            : 'You appear to be offline. Anything you have changed is saved on this device and will sync when you reconnect.'
        }
        action={
          onRetry && (
            <button type="button" className="mx-btn mx-btn-secondary" onClick={onRetry}>
              Try again
            </button>
          )
        }
      />
    )
  }

  return (
    <EmptyState
      title="That didn't work"
      body={message}
      action={
        onRetry && (
          <button type="button" className="mx-btn mx-btn-secondary" onClick={onRetry}>
            Try again
          </button>
        )
      }
    />
  )
}

export function Banner({
  tone = 'warn',
  children,
}: {
  tone?: 'warn' | 'danger' | 'accent' | 'awarded'
  children: ReactNode
}) {
  const tones = {
    warn: { background: 'var(--warn-wash)', color: 'var(--warn)' },
    danger: { background: 'var(--danger-wash)', color: 'var(--danger)' },
    accent: { background: 'var(--accent-wash)', color: 'var(--accent)' },
    awarded: { background: 'var(--awarded-wash)', color: 'var(--awarded)' },
  }
  return (
    <div className="mx-banner" style={tones[tone]} role="status">
      {children}
    </div>
  )
}

/**
 * Offline.
 *
 * A banner, not a blocking dialog. Work carries on against the cache and queued
 * writes drain when the connection returns, so the honest message is what is
 * happening — not an apology and not a wall.
 */
export function OfflineBanner({ queued }: { queued: number }) {
  return (
    <Banner tone="warn">
      Offline
      {queued > 0 && ` — your last ${queued} change${queued === 1 ? '' : 's'} will sync when you reconnect`}
      {queued === 0 && ' — showing what was last saved on this device'}
    </Banner>
  )
}
